/*
====================================================================
                    Componente AlertaVerde
====================================================================
Este componente representa una alerta verde que muestra un mensaje 
de finalización con un título, una descripción, y un temporizador 
opcional que se visualiza mediante un círculo de progreso.

- Muestra un mensaje de alerta de tipo finalización en verde.
- Incluye un círculo de progreso que decrece hasta que se cierra la alerta.
- Permite cerrar la alerta mediante un botón de cierre.

====================================================================
*/

// ------------------------------------------------------------------
//                            IMPORTS
// ------------------------------------------------------------------
// Importación de librerías estándar de React y componentes internos.

import React, { useEffect, useState } from "react";
import ProgressCircle from "../../ProgressCircle";

// ------------------------------------------------------------------
//                       DEFINICIÓN DEL COMPONENTE
// ------------------------------------------------------------------
/**
 * Componente que muestra una alerta de tipo verde.
 * 
 * Este componente muestra una alerta con un título, una descripción, y un 
 * temporizador opcional que se visualiza como un círculo de progreso. La alerta 
 * puede cerrarse manualmente mediante un botón.
 * 
 * @param {object} props
 * @param {string} props.titulo - El título de la alerta.
 * @param {string} props.descripcion - La descripción o mensaje de la alerta.
 * @param {number} [props.tiempo=0] - La duración en segundos del temporizador antes de que la alerta se cierre automáticamente.
 * @param {function} props.handleCerrar - Función que se llama cuando se cierra la alerta.
 * 
 * @returns {JSX.Element} Un componente que muestra una alerta verde.
 */
export default function AlertaVerde({
  titulo = "",
  descripcion = "",
  tiempo = 0,
  handleCerrar = () => {},
}) {
  // Estado para manejar el progreso del círculo
  const [progress, setProgress] = useState(100);
  const [isHovered, setIsHovered] = useState(false);

  // Efecto para manejar el temporizador y actualizar el progreso
  useEffect(() => {
    const intervalTime = 100; // Actualización cada 100ms
    const totalIntervals = (tiempo * 1000) / intervalTime; // Número total de intervalos
    const decrementPerInterval = 100 / totalIntervals; // Cantidad de decremento por intervalo

    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = Math.max(prevProgress - decrementPerInterval, 0);
        return newProgress;
      });
    }, intervalTime);

    return () => clearInterval(interval);
  }, [tiempo]);

  /**
   * Maneja el evento cuando el cursor entra en el botón de cierre.
   */
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  /**
   * Maneja el evento cuando el cursor sale del botón de cierre.
   */
  const handleMouseLeave = () => {
    setIsHovered(false);
  };


  return (
    <div data-figma-id="3170:3174" className="alerta-advertencia-verde">
      <div data-figma-id="3170:3175" className="alerta-contenido">
        <div data-figma-id="3170:3176" className="alerta-icono-2">
        <i
            class={"fi fi-rr-check-circle"}
            style={{ fontSize: 80, color: "#A5FF32" }}
          ></i>
          
        </div>
        <div data-figma-id="3170:3178" className="alerta-textos">
          <p data-figma-id="3170:3179" className="alerta-titulo">
            FINALIZADO
          </p>
          <p data-figma-id="3170:3180" className="alerta-parrafo">
            {titulo}
          </p>
          <p data-figma-id="3170:3181" className="alerta-subtitulo">
            {descripcion}
          </p>
        </div>
        <div data-figma-id="3170:3182" className="alerta-equis-2">
        <i
            class={`fi fi-${isHovered?"sr":"rr"}-circle-xmark`}
            style={{ fontSize: 20, color: "#A5FF32" }}
            onClick={handleCerrar}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          ></i>
          {tiempo > 0 && <ProgressCircle progress={progress} />}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
//                            EXPORTS
// ------------------------------------------------------------------
// Exportación del componente para su uso en otros módulos de la aplicación.
